// Custom ReactFlow node for condition blocks (blue, two output handles)
import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Chip } from '@mui/material';
import { CallSplit as ConditionIcon } from '@mui/icons-material';

const ConditionNode = memo(({ data, selected }) => {
  const debugStep = data._debugStep;
  // For conditions, output is true/false — determines which branch was taken
  const branchTaken = debugStep?.status === 'completed' ? debugStep.output : null;

  return (
    <Box sx={{
      minWidth: 180,
      border: selected ? '2px solid #1565c0' : '1px solid #1976d2',
      borderRadius: 1,
      bgcolor: 'background.paper',
      boxShadow: selected ? 3 : 1,
      position: 'relative',
      ...(debugStep && {
        boxShadow: debugStep.status === 'completed'
          ? '0 0 8px rgba(25,118,210,0.4)' : debugStep.status === 'failed'
            ? '0 0 8px rgba(198,40,40,0.4)' : 1,
      }),
    }}>
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#1976d2', width: 10, height: 10 }}
      />

      {/* Header */}
      <Box sx={{
        px: 1.5, py: 0.75,
        bgcolor: '#1976d2',
        color: 'white',
        borderRadius: '3px 3px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}>
        <ConditionIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
          Condition
        </Typography>
        {debugStep && (
          <Chip
            label={branchTaken === true ? 'TRUE' : branchTaken === false ? 'FALSE' : 'ERR'}
            size="small"
            sx={{
              ml: 'auto',
              height: 16,
              fontSize: '0.55rem',
              fontWeight: 700,
              bgcolor: branchTaken === true ? 'rgba(46,125,50,0.7)'
                : branchTaken === false ? 'rgba(198,40,40,0.7)'
                  : 'rgba(255,255,255,0.25)',
              color: 'white',
            }}
          />
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {data.label || data.blockType}
        </Typography>
        {data.summary && (
          <Typography variant="caption" color="text.secondary">
            {data.summary}
          </Typography>
        )}
      </Box>

      {/* True handle (right) */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{
          background: '#2e7d32',
          width: 10,
          height: 10,
          top: '60%',
          ...(debugStep && branchTaken === true && {
            boxShadow: '0 0 6px rgba(46,125,50,0.8)',
            width: 12,
            height: 12,
          }),
        }}
      />
      <Typography
        variant="caption"
        sx={{ position: 'absolute', right: 14, top: '55%', color: '#2e7d32', fontWeight: 600, fontSize: '0.6rem' }}
      >
        TRUE
      </Typography>

      {/* False handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{
          background: '#c62828',
          width: 10,
          height: 10,
          ...(debugStep && branchTaken === false && {
            boxShadow: '0 0 6px rgba(198,40,40,0.8)',
            width: 12,
            height: 12,
          }),
        }}
      />
      <Typography
        variant="caption"
        sx={{ position: 'absolute', bottom: -16, left: '50%', transform: 'translateX(-50%)', color: '#c62828', fontWeight: 600, fontSize: '0.6rem' }}
      >
        FALSE
      </Typography>
    </Box>
  );
});

ConditionNode.displayName = 'ConditionNode';
export default ConditionNode;
